// The brand registry. Each entry pairs a distributor site's config (`site.ts`)
// with its `BrandConfig` (the canonical `brand.json`).
//
// Keyed by `brandKey` (a stable slug, e.g. `pegasus`) - NOT by `distributorId`.
// A brandKey names a presentation; a distributorId names a tenant of the booking
// API. One tenant may present under several brands (`pegasus` and `pegasus-tr`
// share a UUID and differ only in branding), and a tenant's UUID differs per
// environment. So the UUID addresses an API, and the brandKey addresses the
// bytes: the CDN tree, the resolver, and the email renderer all key on brandKey.
// `distributors.json` maps `brandKey → per-env distributorId` for API calls only.

import {
  brand as austrian,
  site as austrianSite,
} from "../brands/austrian/site";
import {
  brand as azerbaijan,
  site as azerbaijanSite,
} from "../brands/azerbaijan/site";
import {
  brand as brussels,
  site as brusselsSite,
} from "../brands/brussels/site";
import { brand as example, site as exampleSite } from "../brands/example/site";
import { brand as fida, site as fidaSite } from "../brands/fida/site";
import {
  brand as frontier,
  site as frontierSite,
} from "../brands/frontier/site";
import {
  brand as furthrshop,
  site as furthrshopSite,
} from "../brands/furthrshop/site";
import { brand as italo, site as italoSite } from "../brands/italo/site";
import {
  brand as lufthansa,
  site as lufthansaSite,
} from "../brands/lufthansa/site";
import { brand as movetix, site as movetixSite } from "../brands/movetix/site";
import { brand as pegasus, site as pegasusSite } from "../brands/pegasus/site";
import {
  brand as pegasusTr,
  site as pegasusTrSite,
} from "../brands/pegasus-tr/site";
import {
  brand as riyadhair,
  site as riyadhairSite,
} from "../brands/riyadhair/site";
import {
  brand as suncountry,
  site as suncountrySite,
} from "../brands/suncountry/site";
import { brand as swiss, site as swissSite } from "../brands/swiss/site";
import {
  brand as ticketabc,
  site as ticketabcSite,
} from "../brands/ticketabc/site";
import {
  brand as touroptima,
  site as touroptimaSite,
} from "../brands/touroptima/site";
import {
  brand as travelodge,
  site as travelodgeSite,
} from "../brands/travelodge/site";
import { publishBrand } from "@coras-io/brand-tokens/authoring";
import type { Brand } from "./brand";

export const brands = {
  austrian: { brand: publishBrand(austrian), site: austrianSite },
  azerbaijan: { brand: publishBrand(azerbaijan), site: azerbaijanSite },
  brussels: { brand: publishBrand(brussels), site: brusselsSite },
  example: { brand: publishBrand(example), site: exampleSite },
  fida: { brand: publishBrand(fida), site: fidaSite },
  frontier: { brand: publishBrand(frontier), site: frontierSite },
  furthrshop: { brand: publishBrand(furthrshop), site: furthrshopSite },
  italo: { brand: publishBrand(italo), site: italoSite },
  lufthansa: { brand: publishBrand(lufthansa), site: lufthansaSite },
  movetix: { brand: publishBrand(movetix), site: movetixSite },
  pegasus: { brand: publishBrand(pegasus), site: pegasusSite },
  "pegasus-tr": { brand: publishBrand(pegasusTr), site: pegasusTrSite },
  riyadhair: { brand: publishBrand(riyadhair), site: riyadhairSite },
  suncountry: { brand: publishBrand(suncountry), site: suncountrySite },
  swiss: { brand: publishBrand(swiss), site: swissSite },
  ticketabc: { brand: publishBrand(ticketabc), site: ticketabcSite },
  touroptima: { brand: publishBrand(touroptima), site: touroptimaSite },
  travelodge: { brand: publishBrand(travelodge), site: travelodgeSite },
} satisfies Record<string, Brand>;

/** A stable distributor-site slug (the `VITE_DISTRIBUTOR` value), e.g. `"pegasus"`. */
export type BrandKey = keyof typeof brands;

export const brandKeys = Object.keys(brands) as BrandKey[];

/** Resolve a site's brand + config by its `brandKey`, throwing on an unknown slug. */
export function getByBrandKey(key: string): Brand {
  const entry = brands[key as BrandKey];
  if (!entry) {
    throw new Error(`Unknown brand "${key}". Known: ${brandKeys.join(", ")}.`);
  }
  return entry;
}

export type { Brand } from "./brand";
export type {
  SiteConfig,
  SiteLocale,
  SiteCurrency,
  LoyaltyProgramme,
  LoyaltyProgrammeText,
  LinkedTravelNotice,
} from "./types";
export { DEFAULT_LOCALES, DEFAULT_CURRENCIES } from "./types";
export {
  brandAssetUrl,
  sharedAssetUrl,
  brandConfigUrl,
  resolveBrandAssets,
  isBrandKey,
  STATIC_PATHS,
} from "./resolver";
export {
  distributors,
  distributorIdFor,
  type DeployEnv,
  type DistributorManifest,
} from "./manifest";
